// Digital Waste Tracking admin API client (server: /api/v1/dwt, admin only).
//
// Everything shown to staff comes from the server's own mapped wording: Defra's
// raw text never reaches the browser, and neither does a raw request/response.

import { apiClient } from './client';
import { endpoints } from './endpoints';

export type DwtDueState = 'ok' | 'due_within_24h' | 'overdue';

export interface DwtMessage {
  severity: 'error' | 'warning' | 'info';
  field: string | null;
  field_label: string | null;
  message: string;
}

export interface DwtStaffReason {
  code: string;
  message: string;
  fix: string;
}

// -- pre-flight / queue / submit / amend ---------------------------------------------

export type DwtPreflightState =
  | 'will_queue'
  | 'already_queued'
  | 'already_retrying'
  | 'ineligible'
  | 'not_found';

export interface DwtPreflightRow {
  receipt_enc_id: string;
  unique_id: string | null;
  state: DwtPreflightState;
  eligible: boolean;
  reasons: DwtStaffReason[];
}

export interface DwtQueueRow {
  receipt_enc_id: string;
  result: 'queued' | 'already_queued' | 'already_retrying' | 'ineligible' | 'not_found';
  reasons: Array<{ code: string; message: string }>;
}

export type DwtSubmitResultKind =
  | 'not_found'
  | 'ineligible'
  | 'blocked_missing_data'
  | 'refused'
  | 'submitted'
  | 'failed_permanent'
  | 'retry_scheduled'
  | 'unknown_outcome'
  | 'service_charge_unpaid'
  | 'not_amendable';

export interface DwtSubmitResult {
  result: DwtSubmitResultKind;
  reasons: Array<{ code: string; message: string }>;
  staff_reasons: DwtStaffReason[];
  messages: DwtMessage[];
  waste_tracking_id: string | null;
  next_retry_at: string | null;
  attempts: number | null;
}

export async function preflightReceipts(receiptEncIds: string[]): Promise<DwtPreflightRow[]> {
  const res = await apiClient.post<DwtPreflightRow[]>(endpoints.dwt.preflight, {
    receipt_enc_ids: receiptEncIds,
  });
  return res.data;
}

export async function queueReceipts(receiptEncIds: string[]): Promise<DwtQueueRow[]> {
  const res = await apiClient.post<DwtQueueRow[]>(endpoints.dwt.queue, { receipt_enc_ids: receiptEncIds });
  return res.data;
}

export async function submitReceiptNow(encId: string): Promise<DwtSubmitResult> {
  const res = await apiClient.post<DwtSubmitResult>(endpoints.dwt.submit(encId));
  return res.data;
}

export async function sendReceiptAmendment(encId: string): Promise<DwtSubmitResult> {
  const res = await apiClient.post<DwtSubmitResult>(endpoints.dwt.amend(encId));
  return res.data;
}

// -- one receipt's state ---------------------------------------------------------------

export interface DwtSubmissionDto {
  id: number;
  status: string;
  kind: 'create' | 'amend';
  failure_code: string | null;
  environment: 'test' | 'production';
  attempts: number;
  http_status: number | null;
  waste_tracking_id: string | null;
  next_retry_at: string | null;
  submitted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  messages: DwtMessage[];
}

export interface DwtSummary {
  ewc_codes: string[];
  waste_description: string | null;
  weight_kg: number | null;
  weight_is_estimate: boolean | null;
  carrier_vehicle_registration: string | null;
  consignment: { kind: 'code' | 'reason' | 'not_applicable' | 'missing'; value: string | null };
  date_received: string | null;
}

export type NoConsignmentReasonCode = 'NON_HAZ_WASTE_TRANSFER' | 'NO_DOC_WITH_WASTE' | 'HWRC_RECEIPT';

export interface DwtReceiptExtras {
  waste_weight_kg: number | null;
  weight_is_estimate: boolean;
  carrier_vehicle_registration: string | null;
  hazardous_consignment_code: string | null;
  reason_for_no_consignment_code: NoConsignmentReasonCode | null;
}

export interface DwtReceiptState {
  receipt_enc_id: string;
  due_by: string | null;
  due_in: DwtDueState | null;
  dwt_status: string | null;
  waste_tracking_id: string | null;
  eligibility: {
    eligible: boolean;
    reasons: Array<{ code: string; message: string }>;
    staff_reasons: DwtStaffReason[];
  };
  extras: DwtReceiptExtras;
  limits: { max_weight_kg: number };
  profile_hazardous: boolean | null;
  summary: { will_send: DwtSummary | null; sent: DwtSummary | null };
  amendment: {
    required: boolean;
    open: boolean;
    due_by: string | null;
    due_in: DwtDueState | null;
    error_discovered_at: string | null;
    error_notified_at: string | null;
    movement_not_found: boolean;
  };
  current: DwtSubmissionDto | null;
  history: DwtSubmissionDto[];
}

export async function fetchReceiptDwtState(encId: string): Promise<DwtReceiptState> {
  const res = await apiClient.get<DwtReceiptState>(endpoints.dwt.receipt(encId));
  return res.data;
}

export interface DwtExtrasPatch {
  waste_weight_kg?: number | null;
  weight_is_estimate?: boolean;
  carrier_vehicle_registration?: string | null;
  hazardous_consignment_code?: string | null;
  reason_for_no_consignment_code?: NoConsignmentReasonCode | null;
}

export interface DwtExtrasResult {
  changed: boolean;
  amendment_required: boolean;
  values: DwtReceiptExtras;
}

export async function saveReceiptExtras(encId: string, patch: DwtExtrasPatch): Promise<DwtExtrasResult> {
  const res = await apiClient.put<DwtExtrasResult, DwtExtrasPatch>(endpoints.dwt.extras(encId), patch);
  return res.data;
}

export async function recordErrorNotified(
  encId: string,
  notifiedAt?: string,
): Promise<{ notified_at: string; fix_due_by: string }> {
  const res = await apiClient.post<{ notified_at: string; fix_due_by: string }>(
    endpoints.dwt.errorNotified(encId),
    notifiedAt === undefined ? {} : { notified_at: notifiedAt },
  );
  return res.data;
}

// -- delete policy ---------------------------------------------------------------------

export interface DwtDeletePolicy {
  kind: 'free' | 'reported' | 'unresolved';
  reason_required: boolean;
  waste_tracking_id: string | null;
}

export async function fetchDeletePolicy(encId: string): Promise<DwtDeletePolicy> {
  const res = await apiClient.get<DwtDeletePolicy>(endpoints.dwt.deletePolicy(encId));
  return res.data;
}

// -- dashboard ---------------------------------------------------------------------------

export type DwtDashboardKind =
  | 'due_24h'
  | 'overdue'
  | 'unknown_outcome'
  | 'failed_permanent'
  | 'blocked_missing_data'
  | 'amendment_required'
  | 'orphaned';

export interface DwtCounts {
  by_status: Record<string, number>;
  not_reported: number;
  due_within_24h: number;
  overdue: number;
  unknown_outcome: number;
  failed_permanent: number;
  blocked_missing_data: number;
  amendment_required: number;
  orphaned: number;
}

export interface DwtOverview {
  configured: boolean;
  enabled: boolean;
  on: boolean;
  mode: 'off' | 'manual' | 'auto_on_approval';
  environment: 'test' | 'production';
  start_date: string | null;
  last_worker_run_at: string | null;
  next_retry_at: string | null;
  queued: number;
  paused: { active: boolean; since: string | null; explanation: string | null };
  counts: DwtCounts;
}

export interface DwtDashboardRow {
  receipt_enc_id: string;
  unique_id: string;
  receipt_date: string | null;
  car_registration: string | null;
  dwt_status: string | null;
  waste_tracking_id: string | null;
  due_by: string | null;
  due_state: DwtDueState | null;
  attempts: number | null;
  next_retry_at: string | null;
  failure_code: string | null;
  kind: 'create' | 'amend' | null;
  messages: DwtMessage[];
  updated_at: string | null;
  reason?: string | null;
  deleted_by?: string | null;
  note?: string;
}

export interface DwtDashboardPage {
  kind: DwtDashboardKind;
  rows: DwtDashboardRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchDwtOverview(): Promise<DwtOverview> {
  const res = await apiClient.get<DwtOverview>(endpoints.dwt.overview);
  return res.data;
}

export async function fetchDwtDashboardList(kind: DwtDashboardKind, page = 1, limit = 25): Promise<DwtDashboardPage> {
  const res = await apiClient.get<DwtDashboardPage>(
    `${endpoints.dwt.dashboard(kind)}?page=${String(page)}&limit=${String(limit)}`,
  );
  return res.data;
}

export type ResolveUnknownBody =
  | { resolution: 'received'; waste_tracking_id: string; note: string }
  | { resolution: 'not_received'; note: string };

export async function resolveUnknownOutcome(encId: string, body: ResolveUnknownBody): Promise<{ resolved: boolean }> {
  const res = await apiClient.post<{ resolved: boolean }, ResolveUnknownBody>(endpoints.dwt.resolveUnknown(encId), body);
  return res.data;
}

export async function clearDwtPause(): Promise<{ cleared: boolean; released_submissions: number }> {
  const res = await apiClient.post<{ cleared: boolean; released_submissions: number }>(endpoints.dwt.clearPause);
  return res.data;
}

export interface DwtBellCounts {
  unknown_outcomes: number;
  overdue: number;
  paused: boolean;
}

/** null = Digital Waste Tracking is off for the company. */
export async function fetchDwtBell(): Promise<DwtBellCounts | null> {
  const res = await apiClient.get<DwtBellCounts | null>(endpoints.dwt.bell);
  return res.data;
}

// -- reference lists and product profiles -----------------------------------------------

export interface LabelledCode {
  code: string;
  label: string;
}

export interface DwtReference {
  environment: 'test' | 'production';
  /** false until Defra's lists have been downloaded; screens then fall back to typing codes. */
  cached: boolean;
  container_types: LabelledCode[];
  hazard_property_codes: LabelledCode[];
  disposal_recovery_codes: LabelledCode[];
  physical_forms: string[];
  sources: Array<{ value: string; label: string }>;
  no_consignment_reasons: Array<{ value: NoConsignmentReasonCode; label: string }>;
}

export interface DwtEwcLookup {
  cached: boolean;
  codes: Record<string, { well_formed: boolean; known: boolean | null; hazardous: boolean | null }>;
}

export type DwtProfileStatus = 'none' | 'unconfirmed' | 'confirmed';

export interface DwtProfileProduct {
  product_enc_id: string;
  product_name: string;
  product_code: string | null;
  status: DwtProfileStatus;
  ewc_codes: string[];
  blockers: string[];
}

export type DwtPhysicalForm = 'Gas' | 'Liquid' | 'Solid' | 'Powder' | 'Sludge' | 'Mixed';
export type DwtComponentSource = 'NOT_PROVIDED' | 'PROVIDED_WITH_WASTE' | 'GUIDANCE' | 'OWN_TESTING';

export interface DwtProfile {
  product_enc_id: string;
  product_name: string | null;
  ewc_codes: string[];
  waste_description_template: string;
  physical_form: DwtPhysicalForm;
  container_type: string | null;
  number_of_containers_default: number;
  contains_hazardous: boolean;
  contains_pops: boolean;
  hazard_property_codes: string[];
  hazardous_source_of_components: DwtComponentSource;
  pops_source_of_components: DwtComponentSource;
  disposal_recovery_codes: string[];
  default_weight_kg: number | null;
  /** Always true: a defaulted weight is always sent as an estimate. */
  default_weight_is_estimate: true;
  default_consignment_reason: NoConsignmentReasonCode | null;
  confirmed: boolean;
  confirmed_at: string | null;
  confirmed_by: number | null;
  confirmed_by_name: string | null;
  confirm_blockers: string[];
}

export interface DwtProfileInput {
  ewc_codes: string[];
  waste_description_template: string;
  physical_form: DwtPhysicalForm;
  container_type: string | null;
  number_of_containers_default: number;
  contains_hazardous: boolean;
  contains_pops: boolean;
  hazard_property_codes: string[];
  hazardous_source_of_components: DwtComponentSource;
  pops_source_of_components: DwtComponentSource;
  disposal_recovery_codes: string[];
  default_weight_kg: number | null;
  default_weight_is_estimate?: true;
  default_consignment_reason: NoConsignmentReasonCode | null;
}

export async function fetchDwtReference(): Promise<DwtReference> {
  const res = await apiClient.get<DwtReference>(endpoints.dwt.reference);
  return res.data;
}

export async function lookupEwc(codes: string[]): Promise<DwtEwcLookup> {
  const res = await apiClient.get<DwtEwcLookup>(`${endpoints.dwt.referenceEwc}?codes=${encodeURIComponent(codes.join(','))}`);
  return res.data;
}

export async function fetchProfileProducts(): Promise<DwtProfileProduct[]> {
  const res = await apiClient.get<DwtProfileProduct[]>(endpoints.dwt.profileProducts);
  return res.data;
}

export async function fetchProfiles(): Promise<DwtProfile[]> {
  const res = await apiClient.get<DwtProfile[]>(endpoints.dwt.profiles);
  return res.data;
}

export async function saveProfile(productEncId: string, input: DwtProfileInput): Promise<DwtProfile> {
  const res = await apiClient.put<DwtProfile, DwtProfileInput>(endpoints.dwt.profile(productEncId), input);
  return res.data;
}

export async function confirmProfile(productEncId: string): Promise<DwtProfile> {
  const res = await apiClient.post<DwtProfile>(endpoints.dwt.profileConfirm(productEncId));
  return res.data;
}

export async function unconfirmProfile(productEncId: string): Promise<DwtProfile> {
  const res = await apiClient.post<DwtProfile>(endpoints.dwt.profileUnconfirm(productEncId));
  return res.data;
}

// -- production readiness (no Defra call; booleans only) --------------------------------

export interface DwtReadiness {
  ready: boolean;
  company: {
    configured: boolean;
    environment: 'test' | 'production';
    mode: 'off' | 'manual' | 'auto_on_approval';
    enabled: boolean;
    api_code_set: boolean;
    start_date_set: boolean;
    paused: boolean;
    profiles_confirmed: number;
    profiles_unconfirmed: number;
  };
  server: {
    production_credentials_present: boolean;
    test_credentials_present: boolean;
    dwt_allow_production: boolean;
    dwt_allow_test_submissions: boolean;
    outbound_mode: string;
    outbound_live: boolean;
  };
  refusals: Array<{ code: string; message: string }>;
}

export async function fetchDwtReadiness(): Promise<DwtReadiness> {
  const res = await apiClient.get<DwtReadiness>(endpoints.dwt.readiness);
  return res.data;
}
